import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import {
    fetchDomainesRequest,
    fetchThemesRequest,
    fetchThemesByDomaineRequest,
    createDomaineRequest,
    updateDomaineRequest,
    deleteDomaineRequest,
    createThemeRequest,
    updateThemeRequest,
    deleteThemeRequest,

    downloadDomainesTemplateRequest,
    downloadThemesTemplateRequest,
    importDomainesExcelRequest,
    importThemesExcelRequest,
    clearBulkReview,
    clearParamDomainesThemesError,
} from "../../../../redux/actions/paramDomainesThemesActions";

import { API_BASE_URL } from "../../../../../api";
import "../RessourcesStyles/ParamDomainesThemes.css";

const emptyDomaineForm = {
    reference: "",
    nom: "",
    description: "",
    couleur: "#204385",
    actif: true,
    imageFile: null,
};

const emptyThemeForm = {
    reference: "",
    nom: "",
    description: "",
    actif: true,
    domaineId: "",
};

export default function ParamDomainesThemes() {
    const dispatch = useDispatch();

    const {
        domaines = [],
        themes = [],
        themesFiltered = [],
        loading,
        error,
        bulkLoading,
        bulkResult,
        bulkError,
    } = useSelector((state) => state.paramDomainesThemes || {});

    const [tab, setTab] = useState("domaines"); // domaines | themes

    // toolbar / filtre
    const [q, setQ] = useState("");
    const [selectedDomaineId, setSelectedDomaineId] = useState("");

    // modals
    const [openDomaineModal, setOpenDomaineModal] = useState(false);
    const [openThemeModal, setOpenThemeModal] = useState(false);
    const [openBulkModal, setOpenBulkModal] = useState(false);

    // bulk
    const [bulkType, setBulkType] = useState("DOMAINES"); // DOMAINES | THEMES
    const [bulkFile, setBulkFile] = useState(null);

    // forms
    const [editingDomaine, setEditingDomaine] = useState(null);
    const [editingTheme, setEditingTheme] = useState(null);

    const [domaineForm, setDomaineForm] = useState(emptyDomaineForm);
    const [themeForm, setThemeForm] = useState(emptyThemeForm);

    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        dispatch(fetchDomainesRequest());
        dispatch(fetchThemesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (error) {
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: typeof error === "string" ? error : "Une erreur est survenue",
            }).then(() => dispatch(clearParamDomainesThemesError()));
        }
    }, [error, dispatch]);

    useEffect(() => {
        if (bulkError) {
            Swal.fire({
                icon: "error",
                title: "Erreur import",
                text: typeof bulkError === "string" ? bulkError : "Une erreur est survenue",
            });
        }
    }, [bulkError]);

    const themesToUse = useMemo(() => {
        if (Array.isArray(themesFiltered) && themesFiltered.length) return themesFiltered;
        return Array.isArray(themes) ? themes : [];
    }, [themesFiltered, themes]);

    // ====== Filtering ======
    const filteredDomaines = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        if (!t) return domaines;
        return domaines.filter((d) => {
            const ref = (d.reference || "").toLowerCase();
            const nom = (d.nom || "").toLowerCase();
            const desc = (d.description || "").toLowerCase();
            return ref.includes(t) || nom.includes(t) || desc.includes(t);
        });
    }, [domaines, q]);

    const filteredThemes = useMemo(() => {
        const t = (q || "").trim().toLowerCase();
        let list = themesToUse;

        if (selectedDomaineId) {
            list = list.filter((x) => String(x.domaineId) === String(selectedDomaineId));
        }

        if (!t) return list;
        return list.filter((th) => {
            const ref = (th.reference || "").toLowerCase();
            const nom = (th.nom || "").toLowerCase();
            const desc = (th.description || "").toLowerCase();
            const domNom = (th.domaineNom || "").toLowerCase();
            return ref.includes(t) || nom.includes(t) || desc.includes(t) || domNom.includes(t);
        });
    }, [themesToUse, q, selectedDomaineId]);

    // ====== Domain modal handlers ======
    const openCreateDomaine = () => {
        setEditingDomaine(null);
        setDomaineForm(emptyDomaineForm);
        setImagePreview(null);
        setOpenDomaineModal(true);
    };

    const openEditDomaine = (d) => {
        setEditingDomaine(d);
        setDomaineForm({
            reference: d.reference || "",
            nom: d.nom || "",
            description: d.description || "",
            couleur: d.couleur || "#204385",
            actif: d.actif ?? true,
            imageFile: null,
        });
        setImagePreview(d.image ? `${API_BASE_URL}/api/domaines/images/${d.image}` : null);
        setOpenDomaineModal(true);
    };

    const closeDomaineModal = () => {
        setOpenDomaineModal(false);
        setEditingDomaine(null);
        setDomaineForm(emptyDomaineForm);
        setImagePreview(null);
    };

    const onChangeDomaine = (e) => {
        const { name, value, type, checked } = e.target;
        setDomaineForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    };

    const onPickImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setDomaineForm((p) => ({ ...p, imageFile: file }));
        setImagePreview(URL.createObjectURL(file));
    };

    const submitDomaine = (e) => {
        e.preventDefault();

        if (!domaineForm.reference?.trim()) {
            Swal.fire({ icon: "warning", text: "Référence obligatoire." });
            return;
        }
        if (!domaineForm.nom?.trim()) {
            Swal.fire({ icon: "warning", text: "Nom obligatoire." });
            return;
        }

        const payload = {
            reference: domaineForm.reference.trim(),
            nom: domaineForm.nom.trim(),
            description: domaineForm.description?.trim() || "",
            couleur: domaineForm.couleur || "#204385",
            actif: !!domaineForm.actif,
            imageFile: domaineForm.imageFile || null,
        };

        if (editingDomaine?.id) dispatch(updateDomaineRequest(editingDomaine.id, payload));
        else dispatch(createDomaineRequest(payload));

        closeDomaineModal();
    };

    const confirmDeleteDomaine = (d) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer le domaine "${d.nom}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
            confirmButtonColor: "#d33",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteDomaineRequest(d.id));
        });
    };

    // ====== Theme modal handlers ======
    const openCreateTheme = () => {
        setEditingTheme(null);
        setThemeForm({
            ...emptyThemeForm,
            domaineId: selectedDomaineId || "",
        });
        setOpenThemeModal(true);
    };

    const openEditTheme = (t) => {
        setEditingTheme(t);
        setThemeForm({
            reference: t.reference || "",
            nom: t.nom || "",
            description: t.description || "",
            actif: t.actif ?? true,
            domaineId: t.domaineId ? String(t.domaineId) : "",
        });
        setOpenThemeModal(true);
    };

    const closeThemeModal = () => {
        setOpenThemeModal(false);
        setEditingTheme(null);
        setThemeForm(emptyThemeForm);
    };

    const onChangeTheme = (e) => {
        const { name, value, type, checked } = e.target;
        setThemeForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    };

    const submitTheme = (e) => {
        e.preventDefault();

        if (!themeForm.reference?.trim()) {
            Swal.fire({ icon: "warning", text: "Référence obligatoire." });
            return;
        }
        if (!themeForm.nom?.trim()) {
            Swal.fire({ icon: "warning", text: "Nom obligatoire." });
            return;
        }
        if (!themeForm.domaineId) {
            Swal.fire({ icon: "warning", text: "Veuillez sélectionner un domaine." });
            return;
        }

        const payload = {
            reference: themeForm.reference.trim(),
            nom: themeForm.nom.trim(),
            description: themeForm.description?.trim() || "",
            actif: !!themeForm.actif,
            domaineId: Number(themeForm.domaineId),
        };

        if (editingTheme?.id) dispatch(updateThemeRequest(editingTheme.id, payload));
        else dispatch(createThemeRequest(payload));

        closeThemeModal();
    };

    const confirmDeleteTheme = (t) => {
        Swal.fire({
            title: "Supprimer",
            text: `Supprimer le thème "${t.nom}" ?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Non",
            confirmButtonColor: "#d33",
        }).then((res) => {
            if (res.isConfirmed) dispatch(deleteThemeRequest(t.id));
        });
    };

    // ====== Bulk modal ======
    const openBulk = (type) => {
        setBulkType(type);
        setBulkFile(null);
        dispatch(clearBulkReview());
        setOpenBulkModal(true);
    };

    const closeBulk = () => {
        setOpenBulkModal(false);
        setBulkFile(null);
        dispatch(clearBulkReview());
    };

    const downloadTemplate = () => {
        if (bulkType === "DOMAINES") dispatch(downloadDomainesTemplateRequest());
        else dispatch(downloadThemesTemplateRequest());
    };

    const runBulkImport = () => {
        if (!bulkFile) {
            Swal.fire({ icon: "warning", text: "Veuillez sélectionner un fichier Excel." });
            return;
        }
        if (bulkType === "DOMAINES") dispatch(importDomainesExcelRequest(bulkFile));
        else dispatch(importThemesExcelRequest(bulkFile));
    };

    // ====== Themes filtering actions (optional server side) ======
    const applyServerThemesFilter = () => {
        if (!selectedDomaineId) dispatch(fetchThemesRequest());
        else dispatch(fetchThemesByDomaineRequest(selectedDomaineId));
    };

    return (
        <div className="pdt-page">
            <div className="pdt-header">
                <div>
                    <h2>Paramétrage — Domaines & Thèmes</h2>
                </div>

                <div className="pdt-actions">
                    {tab === "domaines" ? (
                        <>
                            <button className="pdt-btn pdt-btn-outline" type="button" onClick={() => openBulk("DOMAINES")}>
                                Ajout massif
                            </button>
                            <button className="pdt-btn pdt-btn-primary" type="button" onClick={openCreateDomaine}>
                                + Ajouter Domaine
                            </button>
                        </>
                    ) : (
                        <>
                            <button className="pdt-btn pdt-btn-outline" type="button" onClick={() => openBulk("THEMES")}>
                                Ajout massif
                            </button>
                            <button className="pdt-btn pdt-btn-primary" type="button" onClick={openCreateTheme}>
                                + Ajouter Thème
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className="pdt-tabs">
                <button className={`pdt-tab ${tab === "domaines" ? "active" : ""}`} type="button" onClick={() => setTab("domaines")}>
                    Domaines
                </button>
                <button className={`pdt-tab ${tab === "themes" ? "active" : ""}`} type="button" onClick={() => setTab("themes")}>
                    Thèmes
                </button>
            </div>

            <div className="pdt-toolbar">
                <input
                    className="pdt-search"
                    placeholder={tab === "domaines" ? "Rechercher (réf, nom, description)..." : "Rechercher (réf, nom, description, domaine)..."}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />

                {tab === "themes" && (
                    <div className="pdt-filter">
                        <select className="pdt-select" value={selectedDomaineId} onChange={(e) => setSelectedDomaineId(e.target.value)}>
                            <option value="">-- Tous les domaines --</option>
                            {domaines.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.nom} ({d.reference})
                                </option>
                            ))}
                        </select>

                        <button className="pdt-btn pdt-btn-secondary" type="button" onClick={applyServerThemesFilter}>
                            Filtrer
                        </button>
                    </div>
                )}

                {/*<div className="pdt-meta">*/}
                {/*    {loading*/}
                {/*        ? "Chargement..."*/}
                {/*        : tab === "domaines"*/}
                {/*            ? `${filteredDomaines.length} domaine(s)`*/}
                {/*            : `${filteredThemes.length} thème(s)`}*/}
                {/*</div>*/}
            </div>

            <div className="pdt-table-wrapper">
                <table className="pdt-table">
                    {tab === "domaines" ? (
                        <>
                            <thead>
                            <tr>
                                <th>Référence</th>
                                <th>Nom</th>
                                <th>Couleur</th>
                                <th>Actif</th>
                                <th>Image</th>
                                <th style={{ width: 160 }}>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredDomaines.map((d) => (
                                <tr key={d.id}>
                                    <td className="mono">{d.reference}</td>
                                    <td>{d.nom}</td>
                                    <td>
                                        <div className="pdt-color-cell">
                                            <span className="pdt-color-dot" style={{ background: d.couleur || "#204385" }} />
                                            <span className="pdt-muted">{d.couleur || "-"}</span>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`pdt-badge ${d.actif ? "ok" : "no"}`}>{d.actif ? "Oui" : "Non"}</span>
                                    </td>
                                    <td>
                                        {d.image ? (
                                            <img
                                                className="pdt-thumb"
                                                src={`${API_BASE_URL}/api/domaines/images/${d.image}`}
                                                alt="domaine"
                                                onError={(e) => (e.currentTarget.style.display = "none")}
                                            />
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td className="pdt-row-actions">
                                        <button className="pdt-link-btn" type="button" onClick={() => openEditDomaine(d)}>
                                            Modifier
                                        </button>
                                        <button className="pdt-link-btn danger" type="button" onClick={() => confirmDeleteDomaine(d)}>
                                            Supprimer
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!filteredDomaines.length && (
                                <tr>
                                    <td colSpan={6} className="pdt-empty">
                                        Aucun domaine
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </>
                    ) : (
                        <>
                            <thead>
                            <tr>
                                <th>Référence</th>
                                <th>Nom</th>
                                <th>Domaine</th>
                                <th>Actif</th>
                                <th style={{ width: 160 }}>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {filteredThemes.map((t) => (
                                <tr key={t.id}>
                                    <td className="mono">{t.reference}</td>
                                    <td>{t.nom}</td>
                                    <td>{t.domaineNom || "-"}</td>
                                    <td>
                                        <span className={`pdt-badge ${t.actif ? "ok" : "no"}`}>{t.actif ? "Oui" : "Non"}</span>
                                    </td>
                                    <td className="pdt-row-actions">
                                        <button className="pdt-link-btn" type="button" onClick={() => openEditTheme(t)}>
                                            Modifier
                                        </button>
                                        <button className="pdt-link-btn danger" type="button" onClick={() => confirmDeleteTheme(t)}>
                                            Supprimer
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!filteredThemes.length && (
                                <tr>
                                    <td colSpan={5} className="pdt-empty">
                                        Aucun thème
                                    </td>
                                </tr>
                            )}
                            </tbody>
                        </>
                    )}
                </table>
            </div>

            {/* =========================
          MODAL DOMAINE (comme Formation)
      ========================= */}
            {openDomaineModal && (
                <div className="pdt-modal-overlay" onClick={closeDomaineModal}>
                    <div className="pdt-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pdt-modal-header">
                            <h3>{editingDomaine ? "Modifier Domaine" : "Ajouter Domaine"}</h3>
                            <button className="pdt-modal-close" type="button" onClick={closeDomaineModal}>
                                ✕
                            </button>
                        </div>

                        <form className="pdt-form" onSubmit={submitDomaine}>
                            <div className="pdt-grid">
                                <div className="pdt-field">
                                    <label>Référence *</label>
                                    <input name="reference" value={domaineForm.reference} onChange={onChangeDomaine} placeholder="DOM-001" />
                                </div>

                                <div className="pdt-field">
                                    <label>Nom *</label>
                                    <input name="nom" value={domaineForm.nom} onChange={onChangeDomaine} placeholder="Ex: Informatique" />
                                </div>

                                <div className="pdt-field full">
                                    <label>Description</label>
                                    <textarea name="description" value={domaineForm.description} onChange={onChangeDomaine} rows={3} placeholder="Description..." />
                                </div>

                                <div className="pdt-field">
                                    <label>Couleur</label>
                                    <input type="color" name="couleur" value={domaineForm.couleur} onChange={onChangeDomaine} className="pdt-color-input" />
                                </div>

                                <div className="pdt-field">
                                    <label className="pdt-check">
                                        <input type="checkbox" name="actif" checked={!!domaineForm.actif} onChange={onChangeDomaine} />
                                        Actif
                                    </label>
                                </div>

                                <div className="pdt-field full">
                                    <label>Image (optionnel)</label>
                                    <input type="file" accept="image/*" onChange={onPickImage} />

                                    {imagePreview && (
                                        <div className="pdt-image-preview">
                                            <img src={imagePreview} alt="preview" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="pdt-modal-actions">
                                <button type="button" className="pdt-btn pdt-btn-outline" onClick={closeDomaineModal} disabled={loading}>
                                    Annuler
                                </button>
                                <button type="submit" className="pdt-btn pdt-btn-primary" disabled={loading}>
                                    {loading ? "En cours..." : editingDomaine ? "Enregistrer" : "Créer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* =========================
          MODAL THEME (comme Formation)
      ========================= */}
            {openThemeModal && (
                <div className="pdt-modal-overlay" onClick={closeThemeModal}>
                    <div className="pdt-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pdt-modal-header">
                            <h3>{editingTheme ? "Modifier Thème" : "Ajouter Thème"}</h3>
                            <button className="pdt-modal-close" type="button" onClick={closeThemeModal}>
                                ✕
                            </button>
                        </div>

                        <form className="pdt-form" onSubmit={submitTheme}>
                            <div className="pdt-grid">
                                <div className="pdt-field">
                                    <label>Référence *</label>
                                    <input name="reference" value={themeForm.reference} onChange={onChangeTheme} placeholder="THE-001" />
                                </div>

                                <div className="pdt-field">
                                    <label>Nom *</label>
                                    <input name="nom" value={themeForm.nom} onChange={onChangeTheme} placeholder="Ex: Formation Java" />
                                </div>

                                <div className="pdt-field full">
                                    <label>Description</label>
                                    <textarea name="description" value={themeForm.description} onChange={onChangeTheme} rows={3} placeholder="Description..." />
                                </div>

                                <div className="pdt-field">
                                    <label>Domaine *</label>
                                    <select name="domaineId" value={themeForm.domaineId} onChange={onChangeTheme}>
                                        <option value="">-- Sélectionner --</option>
                                        {domaines.map((d) => (
                                            <option key={d.id} value={d.id}>
                                                {d.nom} ({d.reference})
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="pdt-field">
                                    <label className="pdt-check">
                                        <input type="checkbox" name="actif" checked={!!themeForm.actif} onChange={onChangeTheme} />
                                        Actif
                                    </label>
                                </div>
                            </div>

                            <div className="pdt-modal-actions">
                                <button type="button" className="pdt-btn pdt-btn-outline" onClick={closeThemeModal} disabled={loading}>
                                    Annuler
                                </button>
                                <button type="submit" className="pdt-btn pdt-btn-primary" disabled={loading}>
                                    {loading ? "En cours..." : editingTheme ? "Enregistrer" : "Créer"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* =========================
          MODAL BULK (comme Formation)
      ========================= */}
            {openBulkModal && (
                <div className="pdt-modal-overlay" onClick={closeBulk}>
                    <div className="pdt-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pdt-modal-header">
                            <h3>Ajout massif (Excel) — {bulkType === "DOMAINES" ? "Domaines" : "Thèmes"}</h3>
                            <button className="pdt-modal-close" type="button" onClick={closeBulk}>
                                ✕
                            </button>
                        </div>

                        <div className="pdt-form">
                            <div className="pdt-grid">
                                <div className="pdt-field full">
                                    <label>Template</label>
                                    <button className="pdt-btn pdt-btn-outline" type="button" onClick={downloadTemplate} disabled={bulkLoading}>
                                        Télécharger template
                                    </button>
                                </div>

                                <div className="pdt-field full">
                                    <label>Fichier Excel</label>
                                    <input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} />
                                </div>
                            </div>

                            <div className="pdt-modal-actions">
                                <button className="pdt-btn pdt-btn-outline" type="button" onClick={closeBulk}>
                                    Fermer
                                </button>
                                <button className="pdt-btn pdt-btn-primary" type="button" onClick={runBulkImport} disabled={bulkLoading}>
                                    {bulkLoading ? "Traitement..." : "Importer"}
                                </button>
                            </div>

                            {bulkResult && (
                                <div className="pdt-import-result">
                                    <div className="pdt-import-summary">
                    <span>
                      <b>Total:</b> {bulkResult.totalRows}
                    </span>
                                        <span>
                      <b>Créés:</b> {bulkResult.createdCount}
                    </span>
                                        <span>
                      <b>Ignorés:</b> {bulkResult.skippedCount}
                    </span>
                                        <span>
                      <b>Erreurs:</b> {(bulkResult.errors || []).length}
                    </span>
                                    </div>

                                    {(bulkResult.errors || []).length > 0 && (
                                        <div className="pdt-import-block">
                                            <div className="pdt-import-title">Erreurs</div>
                                            <div className="pdt-table-wrapper">
                                                <table className="pdt-table">
                                                    <thead>
                                                    <tr>
                                                        <th>Ligne</th>
                                                        <th>Colonne</th>
                                                        <th>Message</th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {bulkResult.errors.map((er, idx) => (
                                                        <tr key={idx}>
                                                            <td>{er.row}</td>
                                                            <td className="mono">{er.column}</td>
                                                            <td>{er.message}</td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
